/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { actionTypes } from '@/reducers/actionTypes/actionTypes';
import { handleError } from '@/utils/handelErrorMessage';
import servicesArr from 'data/services-library.json';
import { toast } from 'sonner';
import { Component } from 'types/api-data';
import { Service } from 'types/services';

import { useComponents } from '@/hooks/useComponents';

interface UseWorkflowDependencyProps {
  component: Component;
  workflowName: string;
  type: 'service' | 'application';
}

export const useWorkflowDependency = ({
  component,
  workflowName,
  type,
}: UseWorkflowDependencyProps) => {
  const [selectedService, setSelectedService] = useState(
    component?.served_by || null
  );
  const [selectedApp, setSelectedApp] = useState(
    component?.apps && component.apps.length > 0 ? component.apps[0] : null
  );
  const [showList, setShowList] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(
    type === 'service' ? !!selectedService : !!selectedApp
  );
  const commandRef = useRef<HTMLDivElement>(null);

  const { messages } = useLanguage();
  const compIntl = messages?.ComponentsPage;

  const {
    dispatch,
    state: { activeServices, associableApps, serviceDependencies },
    componentsActions,
  } = useComponents();

  const matchedService =
    type === 'service'
      ? servicesArr?.services.find(
          (service) => service?.type === selectedService?.type
        )
      : null;

  const matchedServiceData = activeServices?.find(
    (service: Service) =>
      service.component_name === selectedService?.component_name
  );

  useEffect(() => {
    const fetchItems = async () => {
      if (showList) {
        setIsLoading(true);
        try {
          if (type === 'service') {
            await componentsActions.fetchActiveServices();
          } else {
            await componentsActions.fetchAssociableApps();
          }
        } catch (error) {
          console.error('Error fetching items:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchItems();
  }, [showList, type]);

  // `activeServices`/`associableApps` live in shared ComponentsContext state and
  // are otherwise only fetched once the user opens the picker (`showList`).
  // Fetch them on mount too, so an already-selected service/app can resolve its
  // details (e.g. the endpoint URL) without requiring that interaction first.
  useEffect(() => {
    const hasSelection = type === 'service' ? !!selectedService : !!selectedApp;
    if (!hasSelection) {
      setIsInitialLoading(false);
      return;
    }

    const fetchInitialItems = async () => {
      setIsInitialLoading(true);
      try {
        if (type === 'service') {
          await componentsActions.fetchActiveServices();
        } else {
          await componentsActions.fetchAssociableApps();
        }
      } catch (error) {
        console.error('Error fetching items:', error);
      } finally {
        setIsInitialLoading(false);
      }
    };
    fetchInitialItems();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        commandRef.current &&
        !commandRef.current.contains(event.target as Node) &&
        showList
      ) {
        setShowList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showList]);

  // Dedicated service functions
  const handleServiceUpdate = async (newService: any) => {
    setIsUpdating(true);

    let existingDeps = serviceDependencies;
    if (serviceDependencies?.dependencies) {
      existingDeps = serviceDependencies.dependencies;
    }
    if (Array.isArray(existingDeps) && existingDeps[0]?.dependencies) {
      existingDeps = existingDeps[0].dependencies;
    }

    existingDeps = Array.isArray(existingDeps) ? existingDeps : [];

    const filteredDependencies = existingDeps.filter(
      (dep: any) => dep.dependency_type !== 'served_by'
    );

    const payload = [
      ...filteredDependencies,
      {
        target_name: newService?.component_name,
        dependency_type: 'served_by',
      },
    ];

    try {
      const res = await componentsActions.updateWorkflowDependencies(
        workflowName,
        payload
      );

      setSelectedService(newService);
      setShowList(false);
      toast.success(compIntl?.service_associated_successfully);

      dispatch({
        type: actionTypes.FETCH_COMPONENTS_SERVICE_DEPENDENCIES,
        payload: res[0].dependencies,
      });
      await componentsActions.fetchComponents();
    } catch (error) {
      console.error('Error updating service:', error);
      handleError(error, compIntl?.error_associating_service_dependency);
      setShowList(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleServiceRemove = async () => {
    setIsUpdating(true);

    let existingDeps = serviceDependencies;
    if (serviceDependencies?.dependencies) {
      existingDeps = serviceDependencies.dependencies;
    }
    if (Array.isArray(existingDeps) && existingDeps[0]?.dependencies) {
      existingDeps = existingDeps[0].dependencies;
    }

    existingDeps = Array.isArray(existingDeps) ? existingDeps : [];

    const payload = existingDeps.filter(
      (dep: any) => dep.dependency_type !== 'served_by'
    );

    try {
      const res = await componentsActions.updateWorkflowDependencies(
        workflowName,
        payload
      );

      setSelectedService(null);
      toast.success(compIntl?.service_removed_successfully);
      dispatch({
        type: actionTypes.FETCH_COMPONENTS_SERVICE_DEPENDENCIES,
        payload: res[0].dependencies,
      });
      await componentsActions.fetchComponents();
    } catch (error) {
      console.error('Error removing service:', error);
      handleError(error, compIntl?.error_removing_service_dependency);
    } finally {
      setIsUpdating(false);
    }
  };

  // Dedicated application functions
  const handleAppUpdate = async (newApp: any) => {
    setIsUpdating(true);
    setShowList(false);

    try {
      const filteredDependencies = serviceDependencies.filter(
        (dep: any) => dep.dependency_type !== 'comprises'
      );
      const appDependency = {
        source_name: newApp.name,
        dependency_type: 'comprises',
      };

      const updatedDependency = [...filteredDependencies, appDependency];
      const res = await componentsActions.updateComponentDependencies(
        workflowName,
        updatedDependency
      );
      setSelectedApp(newApp);
      setShowList(false);
      toast.success(compIntl?.application_associated_successfully);
      dispatch({
        type: actionTypes.FETCH_COMPONENTS_SERVICE_DEPENDENCIES,
        payload: res[0].dependencies,
      });
      await componentsActions.fetchComponents();
    } catch (error) {
      console.error('Error updating app:', error);
      handleError(
        error,
        `${compIntl?.error_associating_application_dependency}`
      );
      setShowList(false);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAppRemove = async () => {
    try {
      setIsUpdating(true);

      const payload = serviceDependencies?.filter(
        (dep: any) => dep.dependency_type !== 'comprises'
      );

      const res = await componentsActions.updateComponentDependencies(
        workflowName,
        payload
      );

      setSelectedApp(null);
      toast.success(compIntl?.application_removed_successfully);
      dispatch({
        type: actionTypes.FETCH_COMPONENTS_SERVICE_DEPENDENCIES,
        payload: res[0].dependencies,
      });
      await componentsActions.fetchComponents();
    } catch (error) {
      console.error('Error removing app:', error);
      handleError(error, `${compIntl?.error_removing_application_dependency}`);
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    selectedService,
    selectedApp,
    showList,
    isLoading,
    isUpdating,
    isInitialLoading,
    matchedService,
    matchedServiceData,
    activeServices,
    associableApps,
    commandRef,
    setShowList,
    // Return specific functions based on type
    handleUpdate: type === 'service' ? handleServiceUpdate : handleAppUpdate,
    handleRemove: type === 'service' ? handleServiceRemove : handleAppRemove,
  };
};
